// Device-local input, never a cloud backup. Server context must be verified before reading.
import { localize, translationKey } from '../i18n';
export const REGISTRATION_DRAFT_KEY = 'pill_reminder_registration_draft';
export const REGISTRATION_DRAFT_EVENT = 'kkomok-registration-draft-changed';
export const DRAFT_DAYS_MS = 7 * 86400000;
const RESULT_AGE_MS = 30 * 86400000;
const MAX_SIZE = 180000;
let owner = null;
let generation = 0;
const id = () => window.crypto?.randomUUID?.() || `draft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
const fields = {
    nameDraft: 500, startDate: 10, durationDays: 'number', times: 'times', dosageMethod: 40,
    alertType: 40, alarmVolume: 'number', reminderEnabled: 'boolean', reminderIntervalMinutes: 'number',
    maxReminderMinutes: 'number', usagePeriodType: 16, nextKey: 'number', entryMode: 16, manualStep: 16,
    photoCandidatesConfirmed: 'boolean', hasTemporaryPhotos: 'boolean', scheduleSource: 16,
    showPerMedicationPeriod: 'boolean', showPerMedicationReminder: 'boolean',
    selectedPeriodMedKey: 'key', selectedReminderMedKey: 'key', periodConfiguredMedKeys: 'keys',
    reminderConfiguredMedKeys: 'keys', selectedExistingTimeKey: 100,
    customDurationDraft: 20,
};
const medFields = { key: 'key', name: 500, memo: 1000, itemSeq: 80, medicineSource: 40,
    dosagePerTime: 'number', photoOriginalName: 500, durationDays: 'number', timesPerDay: 'number',
    suggestedTimes: 'times', timesSource: 40, durationInvalid: 'boolean', timesInvalid: 'boolean', regimenText: 500 };
const scheduleFields = { ...fields, frequencyType: 40, specificDays: 'strings', hourlyInterval: 'number',
    intervalDays: 'number', cycleDaysOn: 'number', cycleDaysOff: 'number', monthlyDays: 'keys',
    endDate: 10, indefinite: 'boolean', category: 40, intakeForm: 40, source: 20, groupId: 100 };
const requestFields = new Set(['name', 'memo', 'category', 'intakeForm', 'itemSeq', 'medicineSource', 'frequencyType',
    'specificDays', 'hourlyInterval', 'intervalDays', 'cycleDaysOn', 'cycleDaysOff', 'monthlyDays', 'dosageMethod',
    'startDate', 'endDate', 'times', 'dosagePerTime', 'alertType', 'alarmVolume', 'initialIntervalMinutes',
    'escalationAfterMinutes', 'escalatedIntervalMinutes', 'maxReminderMinutes', 'totalStock', 'stockAlertThreshold']);
function object(value) { return value && typeof value === 'object' && !Array.isArray(value); }
function validRequest(request) {
    return Array.isArray(request) && request.length > 0 && request.length <= 200
        && request.every(m => object(m) && typeof m.name === 'string' && m.name.length <= 500
            && Array.isArray(m.times) && m.times.length <= 10 && m.times.every(t => typeof t === 'string' && t.length <= 10)
            && Object.keys(m).every(key => requestFields.has(key))
            && Object.values(m).every(v => v === null || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v))
                || (typeof v === 'string' && v.length <= 1000 && !/^(blob:|data:)/.test(v)) || Array.isArray(v))
            && Object.entries(m).filter(([, v]) => Array.isArray(v)).every(([key, v]) =>
                ['times', 'specificDays', 'monthlyDays'].includes(key) && v.length <= 200
                && v.every(item => (typeof item === 'string' && item.length <= 100) || (typeof item === 'number' && Number.isFinite(item)))));
}
function pick(value, schema) {
    const result = {};
    if (!object(value)) return result;
    for (const [key, rule] of Object.entries(schema)) {
        const v = value[key];
        if (v === null) { result[key] = null; continue; }
        if (typeof rule === 'number' && typeof v === 'string' && v.length <= rule && !/^(blob:|data:)/.test(v)) result[key] = v;
        if (rule === 'number' && ((typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && /^-?\d{0,6}(\.\d{0,2})?$/.test(v)))) result[key] = v;
        if (rule === 'boolean' && typeof v === 'boolean') result[key] = v;
        if (rule === 'key' && (Number.isFinite(v) || (typeof v === 'string' && v.length <= 100))) result[key] = v;
        if (['times', 'keys', 'strings'].includes(rule) && Array.isArray(v) && v.length <= (rule === 'times' ? 10 : 200)
            && v.every(item => (rule === 'keys' && Number.isFinite(item)) || (typeof item === 'string' && item.length <= 100))) result[key] = v.slice();
    }
    return result;
}
export function cleanRegistrationInput(input) {
    const result = pick(input, fields);
    result.medications = (Array.isArray(input?.medications) ? input.medications : []).slice(0, 200)
        .filter(m => object(m) && typeof m.name === 'string' && m.name.length <= 500)
        .map(m => ({ ...pick(m, medFields), schedule: pick(m.schedule, scheduleFields) }));
    for (const field of ['selectedReminderExistingTimeKeys', 'groupCustomDurationDraft', 'manualPeriodCustomDraft']) {
        const values = object(input?.[field]) ? Object.entries(input[field]).slice(0, 200) : [];
        result[field] = Object.fromEntries(values.filter(([key, value]) => /^[a-zA-Z0-9_-]{1,100}$/.test(key)
            && !['__proto__', 'constructor', 'prototype'].includes(key) && typeof value === 'string' && value.length <= 100));
    }
    if (object(input?.pendingReviewEdit) && ['name', 'medicine', 'schedule', 'alert'].includes(input.pendingReviewEdit.section)
        && result.medications.some(m => m.key === input.pendingReviewEdit.key) && object(input.pendingReviewEdit.draft)) {
        result.pendingReviewEdit = { section: input.pendingReviewEdit.section, key: input.pendingReviewEdit.key,
            draft: { ...pick(input.pendingReviewEdit.draft, { ...medFields, category: 40, intakeForm: 40 }), schedule: pick(input.pendingReviewEdit.draft.schedule, scheduleFields) } };
    }
    if (!['manual', 'photo', 'review'].includes(result.entryMode)) result.entryMode = 'manual';
    if (!['names', 'period', 'reminder', 'schedule'].includes(result.manualStep)) result.manualStep = 'names';
    if (!result.medications.length) { result.entryMode = 'manual'; result.manualStep = 'names'; }
    if (result.entryMode === 'photo' && result.medications.length) result.entryMode = 'manual';
    // Never restore an unfinished modal or an old interaction/analysis authorization.
    return result;
}
export function meaningfulRegistrationInput(input) {
    return Boolean(input?.nameDraft?.trim() || input?.medications?.some(m => m.name?.trim()));
}
function restoreStage(draft) {
    const input = cleanRegistrationInput(draft.input);
    const hasNames = input.medications.length > 0 && input.medications.every(m => m.name.trim());
    if (!hasNames || (draft.source === 'photo' && !input.photoCandidatesConfirmed)) {
        input.entryMode = 'manual'; input.manualStep = 'names';
    } else if (!draft.path.includes('AS_NEEDED')) {
        const periodReady = input.showPerMedicationPeriod
            ? input.medications.every(m => input.periodConfiguredMedKeys?.includes(m.key))
            : ['ongoing', 'fixed'].includes(input.usagePeriodType);
        const remindersReady = input.showPerMedicationReminder
            ? input.medications.every(m => input.reminderConfiguredMedKeys?.includes(m.key))
            : input.times?.length > 0 && input.times.every(t => /^\d{2}:\d{2}$/.test(t));
        if (!periodReady && (input.entryMode === 'review' || ['reminder', 'schedule'].includes(input.manualStep))) {
            input.entryMode = 'manual'; input.manualStep = 'period';
        } else if (!remindersReady && input.entryMode === 'review') {
            input.entryMode = 'manual'; input.manualStep = 'reminder';
        }
    }
    return input;
}
export function registrationPath(path) {
    if (typeof path !== 'string' || path.split('?')[0] !== '/medications/new-bundle') return null;
    const params = new URLSearchParams(path.split('?')[1]);
    return `/medications/new-bundle?mode=${params.get('mode') === 'photo' ? 'photo' : 'manual'}${params.get('frequency') === 'AS_NEEDED' ? '&frequency=AS_NEEDED' : ''}`;
}
function notify() { window.dispatchEvent(new Event(REGISTRATION_DRAFT_EVENT)); }
function rawRead() {
    const raw = localStorage.getItem(REGISTRATION_DRAFT_KEY);
    if (!raw) return null;
    if (raw.length > MAX_SIZE) throw new Error('invalid draft');
    const draft = JSON.parse(raw);
    if (!object(draft) || draft.version !== 1 || !['editing', 'pending', 'completed'].includes(draft.status)
        || typeof draft.id !== 'string' || !/^[a-zA-Z0-9_-]{8,80}$/.test(draft.id) || !Number.isInteger(draft.revision)
        || typeof draft.flowId !== 'string' || !/^[a-zA-Z0-9_-]{8,100}$/.test(draft.flowId)
        || !['manual', 'photo'].includes(draft.source)
        || typeof draft.uuid !== 'string' || typeof draft.context !== 'string' || !registrationPath(draft.path)
        || !Number.isFinite(draft.modifiedAt) || !Number.isFinite(draft.createdAt)) throw new Error('invalid draft');
    return draft;
}
export function setRegistrationOwner(uuid, context) {
    if (!uuid || !/^[a-f0-9]{64}$/.test(context || '')) return null;
    if (!owner || owner.uuid !== uuid || owner.context !== context) generation++;
    owner = { uuid, context, generation };
    readRegistrationDraft();
    notify();
    return { ...owner };
}
export function registrationOwner() { return owner && { ...owner }; }
export function invalidateRegistrationOwner({ clear = true } = {}) {
    owner = null; generation++;
    if (clear) { try { localStorage.removeItem(REGISTRATION_DRAFT_KEY); } catch {} }
    notify();
}
export function readRegistrationDraft() {
    if (!owner) return null;
    try {
        const draft = rawRead();
        if (!draft) return null;
        const elapsed = Date.now() - draft.modifiedAt;
        // A clock rollback is not proof that an unresolved POST was never saved.
        const age = draft.status === 'pending' ? Math.max(0, elapsed) : elapsed;
        if (draft.uuid !== owner.uuid || draft.context !== owner.context || draft.status === 'completed'
            || age < 0 || age > (draft.status === 'pending' ? RESULT_AGE_MS : DRAFT_DAYS_MS)) {
            localStorage.removeItem(REGISTRATION_DRAFT_KEY); return null;
        }
        if (draft.status === 'pending') {
            if (typeof draft.requestId !== 'string' || !/^[a-zA-Z0-9_-]{8,80}$/.test(draft.requestId)) throw new Error('invalid request');
            if (draft.request && (age > DRAFT_DAYS_MS || !validRequest(draft.request))) {
                delete draft.request; delete draft.input;
                draft.revision++;
                try { localStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify(draft)); } catch {}
            }
            return draft;
        }
        draft.input = restoreStage(draft);
        return meaningfulRegistrationInput(draft.input) ? draft : null;
    } catch { try { localStorage.removeItem(REGISTRATION_DRAFT_KEY); } catch {} return null; }
}
function sameRevision(current, expected) {
    return (!current && !expected) || (current && expected && current.id === expected.id && current.revision === expected.revision && current.writeId === expected.writeId);
}
export function writeRegistrationDraft(expected, input, meta, lease = owner) {
    if (!lease || lease.generation !== owner?.generation || lease.uuid !== owner.uuid || lease.context !== owner.context) return { ok: false, conflict: true };
    try {
        let current = rawRead();
        if (current?.status === 'completed' && !expected) current = null;
        if (!sameRevision(current, expected)) return { ok: false, conflict: true };
        if (current?.status === 'pending' || current?.status === 'completed') return { ok: false, conflict: true };
        const clean = cleanRegistrationInput(input);
        if (!meaningfulRegistrationInput(clean)) {
            localStorage.removeItem(REGISTRATION_DRAFT_KEY); notify(); return { ok: true, draft: null };
        }
        const path = registrationPath(meta.path);
        if (!path) return { ok: false };
        const signature = JSON.stringify(clean);
        // Stage navigation is preserved, but does not extend retention on its own.
        const content = ({ entryMode, manualStep, selectedPeriodMedKey, selectedReminderMedKey, ...values }) => JSON.stringify(values);
        const changed = !current || content(clean) !== content(current.input);
        if (current && signature === JSON.stringify(current.input) && path === current.path) return { ok: true, draft: current };
        const now = Date.now();
        const draft = { version: 1, uuid: lease.uuid, context: lease.context, id: current?.id || id(),
            revision: (current?.revision || 0) + 1, writeId: id(), status: 'editing', path, flowId: meta.flowId,
            source: meta.source === 'photo' ? 'photo' : 'manual', createdAt: current?.createdAt || now,
            modifiedAt: changed ? now : current.modifiedAt, input: clean };
        const raw = JSON.stringify(draft);
        if (raw.length > MAX_SIZE) return { ok: false };
        localStorage.setItem(REGISTRATION_DRAFT_KEY, raw);
        if (localStorage.getItem(REGISTRATION_DRAFT_KEY) !== raw) return { ok: false };
        notify(); return { ok: true, draft };
    } catch { return { ok: false }; }
}
export function discardRegistrationDraft(expected) {
    try {
        const current = rawRead();
        if (!sameRevision(current, expected) || current?.status === 'pending') return false;
        localStorage.removeItem(REGISTRATION_DRAFT_KEY); notify(); return true;
    } catch { return false; }
}
export function freezeRegistrationRequest(expected, requestId, request) {
    try {
        const current = readRegistrationDraft();
        if (!sameRevision(current, expected) || !current || !validRequest(request)) return { ok: false };
        if (current.status === 'pending') return { ok: current.requestId === requestId && JSON.stringify(current.request) === JSON.stringify(request), draft: current };
        const draft = { ...current, status: 'pending', requestId, request: JSON.parse(JSON.stringify(request)), modifiedAt: Date.now(), writeId: id(), revision: current.revision + 1 };
        const raw = JSON.stringify(draft);
        if (raw.length > MAX_SIZE) return { ok: false };
        localStorage.setItem(REGISTRATION_DRAFT_KEY, raw);
        if (localStorage.getItem(REGISTRATION_DRAFT_KEY) !== raw) return { ok: false };
        notify(); return { ok: true, draft };
    } catch { return { ok: false }; }
}
export function resolveRegistrationRequest(expected, saved) {
    try {
        const current = readRegistrationDraft();
        if (!sameRevision(current, expected) || current?.status !== 'pending') return false;
        if (saved) {
            // Write a non-resumable receipt before attempting deletion: failure cannot resurrect the form.
            localStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify({ ...current, status: 'completed', request: undefined, input: undefined, revision: current.revision + 1 }));
            try { localStorage.removeItem(REGISTRATION_DRAFT_KEY); } catch {}
        } else {
            localStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify({ ...current, status: 'editing', requestId: undefined, request: undefined, revision: current.revision + 1 }));
        }
        notify(); return true;
    } catch { return false; }
}
export function draftSummary(draft) {
    if (draft?.status === 'pending') return localize('저장 요청 후 결과 확인이 필요해요');
    const count = draft?.input?.medications?.filter(m => m.name?.trim()).length || 0;
    const asNeeded = draft?.path?.includes('AS_NEEDED');
    const stages = { names: translationKey('이름 입력 중'), period: translationKey('복용 기간 입력 중'), reminder: translationKey('알림 입력 중'), schedule: translationKey('등록 내용 확인 중') };
    return count ? localize(asNeeded ? '필요할 때 쓰는 약 {count}개 · {stage}' : '약 {count}개 · {stage}',
        { count, stage: localize(stages[draft.input.manualStep] || stages.names) })
        : localize(asNeeded ? '필요할 때 쓰는 약 이름 입력 중' : '약 이름 입력 중');
}
